import React, { Fragment, memo, useCallback, useContext, useMemo, useState } from 'react'
import type { StyledSvgComponent } from '../../../../types/svg'
import {
    BackButton,
    BackIconContainer,
    ButtonWrapper,
    CenterSubContainer,
    DownArrow,
    GhostButtonContent,
    GhostButtonWrapper,
    HeaderBar,
    HeaderBarContent,
    IconImage,
    LeftSubContainer,
    LoggedInButtonWrapper,
    LoginButtonText,
    LogoImage,
    LogoWrapper,
    MenuIconWrapper,
    MenuImage,
    NavigationItem,
    PopUpBox,
    PopUpBoxIcon,
    PopUpBoxItem,
    PopUpBoxWrapper,
    ProfileContent,
    RightSubContainer,
    SideBarItemIcon,
    SideBarItemWrapper,
    StickyContainer,
    UserIcon,
    UserMenu,
    UserMenuLowerContent,
    WishlistButtonWrapper,
} from './styles'

import { Header, ScreenContext, Sidebar } from 'react-components'
import GhostButton from '../../../common/ghost-button'
import ExternalLink from '../../../common/external-link'
import SidebarItem from 'react-components/dist/components/sidebar-mobile/sidebar-item'
import { useTranslation } from '../../../../hooks/locale'
import NavigationSectionTransport from '../../../../transports/common/navigation-section-transport'
import { getContent } from '../../../utils'
import CenterContainer from './center-container'
import UserCheckIcon from '../../../../assets/icons/userbar-icons/user-check.svg'
import ChevronDownIcon from '../../../../assets/icons/chevron-down.svg'
import UserHeaderIcon from '../../../../assets/icons/userbar-icons/user.svg'
import NavigationIcon from '../../../../assets/icons/userbar-icons/navigation.svg'
import CloseRegularIcon from '../../../../assets/icons/close-regular.svg'

interface Props {
    children: React.JSX.Element | React.ReactNode
    isLoggedIn: boolean
    isLandlordRole: boolean
    username?: string
    headerSection: NavigationSectionTransport
    showMenuSidebar?: boolean
    showBackButton?: boolean
    showMiniLogo?: boolean
}

const HeaderSection: React.FC<Props> = ({
    children,
    isLoggedIn,
    isLandlordRole = false,
    username,
    headerSection,
    showMenuSidebar,
    showBackButton: _showBackButton,
    showMiniLogo: _showMiniLogo,
}) => {
    const {
        vacationIdeasButton,
        offersButton,
        buyPropertyButton,
        helpButton,
        wishlistButton,
        logoutButton,
        loginButton,
        logo,
        userBookingsButton,
        guestInquiriesButton,
        guestProfileButton,
        landlordAreaButton,
        listRentalButton,
    } = headerSection.navigation

    const childrenContent = children

    const centerContent = useMemo(() => getContent(childrenContent, CenterContainer), [childrenContent])

    const { isDesktop, isMobile } = useContext(ScreenContext)
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const [isUserSidebarOpen, setIsUserSidebarOpen] = useState(false)
    const toggleSidebar = useCallback(() => setIsSidebarOpen(!isSidebarOpen), [isSidebarOpen, setIsSidebarOpen])

    const toggleUserSidebar = useCallback(
        () => setIsUserSidebarOpen(!isUserSidebarOpen),
        [isUserSidebarOpen, setIsUserSidebarOpen],
    )

    const [popUpBoxVisible, setPopUpBoxVisible] = useState(false)
    const setPopUpBoxVisibility = useCallback(
        (isVisible: boolean) => setPopUpBoxVisible(isVisible),
        [setPopUpBoxVisible],
    )

    const goBack = useCallback(() => {
        globalThis.history.back()
    }, [])

    const listRentalClickHandler = useCallback(() => {
        if (listRentalButton.url) {
            globalThis.location.href = listRentalButton.url
        }
    }, [])

    const { t } = useTranslation()

    return (
        <StickyContainer>
            <Header id="header">
                <HeaderBar>
                    <HeaderBarContent>
                        <LeftSubContainer>
                            {isMobile ? (
                                <BackButton onClick={goBack}>
                                    <BackIconContainer />
                                </BackButton>
                            ) : (
                                !!logo?.image?.url && (
                                    <LogoWrapper href={logo.url}>
                                        <LogoImage
                                            src={logo.image.url}
                                            alt={logo.image.alt ? logo.image.alt : 'Logo'}
                                        />
                                    </LogoWrapper>
                                )
                            )}
                            <CenterSubContainer isSearchbarPresent={!showMenuSidebar}>
                                {centerContent}
                            </CenterSubContainer>
                        </LeftSubContainer>
                        <RightSubContainer>
                            {isMobile && !!listRentalButton?.url && (isLoggedIn ? !isLandlordRole : true) && (
                                <GhostButtonWrapper>
                                    <GhostButton onClick={listRentalClickHandler} padding={'0.3125rem'} hoverEffect>
                                        <GhostButtonContent>{listRentalButton.text}</GhostButtonContent>
                                    </GhostButton>
                                </GhostButtonWrapper>
                            )}
                            <WishlistButtonWrapper>
                                <NavigationItem href={wishlistButton.url} key={wishlistButton.text}>
                                    {!!wishlistButton?.image && (
                                        <IconImage
                                            src={wishlistButton.image.url}
                                            alt={wishlistButton.image.alt ? wishlistButton.image.alt : 'heart icon'}
                                        />
                                    )}
                                    {showMenuSidebar
                                        ? !isMobile && `${wishlistButton.text}`
                                        : isDesktop && `${wishlistButton.text}`}
                                </NavigationItem>
                            </WishlistButtonWrapper>
                            {isLoggedIn ? (
                                <Fragment>
                                    {isMobile && (
                                        <ButtonWrapper onClick={toggleUserSidebar}>
                                            <IconImage as={UserCheckIcon as StyledSvgComponent} />
                                        </ButtonWrapper>
                                    )}
                                    {!isMobile && (
                                        <LoggedInButtonWrapper
                                            onMouseEnter={() => setPopUpBoxVisibility(true)}
                                            onMouseLeave={() => setPopUpBoxVisibility(false)}
                                        >
                                            <UserIcon as={UserCheckIcon as StyledSvgComponent} />

                                            <UserMenu>
                                                <UserMenuLowerContent>
                                                    {showMenuSidebar ? `${username}` : isDesktop && `${username}`}
                                                    &nbsp;&nbsp;
                                                    <DownArrow as={ChevronDownIcon as StyledSvgComponent} />
                                                </UserMenuLowerContent>
                                            </UserMenu>

                                            {popUpBoxVisible && (
                                                <PopUpBox>
                                                    <PopUpBoxWrapper>
                                                        {!!userBookingsButton.image && (
                                                            <PopUpBoxIcon src={userBookingsButton.image.url} />
                                                        )}
                                                        <PopUpBoxItem href={userBookingsButton.url}>
                                                            {userBookingsButton.text}
                                                        </PopUpBoxItem>
                                                    </PopUpBoxWrapper>

                                                    <PopUpBoxWrapper>
                                                        {!!guestInquiriesButton.image && (
                                                            <PopUpBoxIcon src={guestInquiriesButton.image.url} />
                                                        )}
                                                        <PopUpBoxItem href={guestInquiriesButton.url}>
                                                            {guestInquiriesButton.text}
                                                        </PopUpBoxItem>
                                                    </PopUpBoxWrapper>

                                                    <PopUpBoxWrapper>
                                                        {!!guestProfileButton.image && (
                                                            <PopUpBoxIcon src={guestProfileButton.image.url} />
                                                        )}
                                                        <PopUpBoxItem href={guestProfileButton.url}>
                                                            {guestProfileButton.text}
                                                        </PopUpBoxItem>
                                                    </PopUpBoxWrapper>

                                                    <PopUpBoxWrapper>
                                                        {!!logoutButton.image && (
                                                            <PopUpBoxIcon src={logoutButton.image.url} />
                                                        )}
                                                        <PopUpBoxItem href={logoutButton.url}>
                                                            {logoutButton.text}
                                                        </PopUpBoxItem>
                                                    </PopUpBoxWrapper>
                                                </PopUpBox>
                                            )}
                                        </LoggedInButtonWrapper>
                                    )}
                                </Fragment>
                            ) : (
                                <ProfileContent>
                                    {isMobile ? (
                                        <ExternalLink
                                            href={loginButton.url}
                                            as={ButtonWrapper}
                                            noCrawl={loginButton.noCrawl}
                                        >
                                            {loginButton.image?.url ? (
                                                <IconImage src={loginButton.image.url} aria-label={'user icon'} />
                                            ) : (
                                                <IconImage as={UserHeaderIcon as StyledSvgComponent} />
                                            )}
                                        </ExternalLink>
                                    ) : isDesktop ? (
                                        <ExternalLink
                                            href={loginButton.url}
                                            as={GhostButtonWrapper}
                                            noCrawl={loginButton.noCrawl}
                                        >
                                            <IconImage as={UserHeaderIcon as StyledSvgComponent} />
                                            <LoginButtonText>{loginButton.text}</LoginButtonText>
                                        </ExternalLink>
                                    ) : (
                                        <NavigationItem href={loginButton.url} key={loginButton.text}>
                                            {!!loginButton.image && (
                                                <IconImage
                                                    src={loginButton.image.url}
                                                    alt={loginButton.image.alt ? loginButton.image.alt : 'user icon'}
                                                />
                                            )}
                                            {loginButton.text}
                                        </NavigationItem>
                                    )}
                                </ProfileContent>
                            )}
                            {!isMobile && !!listRentalButton?.url && (isLoggedIn ? !isLandlordRole : true) && (
                                <GhostButtonWrapper>
                                    <GhostButton onClick={listRentalClickHandler} padding={'0.3125rem'} hoverEffect>
                                        <GhostButtonContent>{listRentalButton.text}</GhostButtonContent>
                                    </GhostButton>
                                </GhostButtonWrapper>
                            )}
                            {isMobile && showMenuSidebar && (
                                <MenuIconWrapper onClick={toggleSidebar}>
                                    <IconImage as={NavigationIcon as StyledSvgComponent} />
                                </MenuIconWrapper>
                            )}
                        </RightSubContainer>
                    </HeaderBarContent>
                </HeaderBar>

                {isMobile && showMenuSidebar && (
                    <Sidebar
                        visible={isSidebarOpen && !isDesktop}
                        menuIcon={
                            logo?.image?.url ? (
                                <MenuImage src={logo.image.url} alt={'navigation icon'} />
                            ) : (
                                <MenuImage as={NavigationIcon as StyledSvgComponent} />
                            )
                        }
                        topText={t('menu')}
                        toggleSidebar={toggleSidebar}
                        closeIcon={<IconImage as={CloseRegularIcon as StyledSvgComponent} />}
                        leftBorder
                    >
                        {!!vacationIdeasButton?.url && (
                            <SideBarItemWrapper key={vacationIdeasButton.url}>
                                <SidebarItem
                                    link={vacationIdeasButton.url}
                                    text={vacationIdeasButton.text}
                                    toggleSidebar={toggleSidebar}
                                />
                            </SideBarItemWrapper>
                        )}
                        {!!offersButton?.url && (
                            <SideBarItemWrapper key={offersButton.url}>
                                <SidebarItem
                                    link={offersButton.url}
                                    text={offersButton.text}
                                    toggleSidebar={toggleSidebar}
                                />
                            </SideBarItemWrapper>
                        )}
                        {!!buyPropertyButton?.url && (
                            <SideBarItemWrapper key={buyPropertyButton.url}>
                                <SidebarItem
                                    link={buyPropertyButton.url}
                                    text={buyPropertyButton.text}
                                    toggleSidebar={toggleSidebar}
                                />
                            </SideBarItemWrapper>
                        )}
                        {!!helpButton?.url && (
                            <SideBarItemWrapper key={helpButton.url}>
                                <SidebarItem
                                    link={helpButton.url}
                                    text={helpButton.text}
                                    toggleSidebar={toggleSidebar}
                                />
                            </SideBarItemWrapper>
                        )}
                        {!!landlordAreaButton?.url && (
                            <SideBarItemWrapper key={landlordAreaButton.url}>
                                <SidebarItem
                                    link={landlordAreaButton.url}
                                    text={landlordAreaButton.text}
                                    toggleSidebar={toggleSidebar}
                                />
                            </SideBarItemWrapper>
                        )}
                    </Sidebar>
                )}
                {isMobile && isLoggedIn && (
                    <Sidebar
                        visible={isUserSidebarOpen && !isDesktop}
                        menuIcon={<MenuImage as={UserHeaderIcon as StyledSvgComponent} />}
                        topText={t('myAccount')}
                        toggleSidebar={toggleUserSidebar}
                        closeIcon={<IconImage as={CloseRegularIcon as StyledSvgComponent} />}
                        leftBorder
                    >
                        {!!userBookingsButton?.url && (
                            <SideBarItemWrapper>
                                {!!userBookingsButton.image && <SideBarItemIcon src={userBookingsButton.image.url} />}
                                <SidebarItem
                                    link={userBookingsButton.url}
                                    text={userBookingsButton.text}
                                    toggleSidebar={toggleSidebar}
                                />
                            </SideBarItemWrapper>
                        )}
                        {!!guestInquiriesButton?.url && (
                            <SideBarItemWrapper>
                                {!!guestInquiriesButton.image && (
                                    <SideBarItemIcon src={guestInquiriesButton.image.url} />
                                )}
                                <SidebarItem
                                    link={guestInquiriesButton.url}
                                    text={guestInquiriesButton.text}
                                    toggleSidebar={toggleSidebar}
                                />
                            </SideBarItemWrapper>
                        )}
                        {!!guestProfileButton?.url && (
                            <SideBarItemWrapper>
                                {!!guestProfileButton.image && <SideBarItemIcon src={guestProfileButton.image.url} />}
                                <SidebarItem
                                    link={guestProfileButton.url}
                                    text={guestProfileButton.text}
                                    toggleSidebar={toggleUserSidebar}
                                />
                            </SideBarItemWrapper>
                        )}
                        {!!logoutButton?.url && (
                            <SideBarItemWrapper>
                                {!!logoutButton.image && <SideBarItemIcon src={logoutButton.image.url} />}
                                <SidebarItem
                                    link={logoutButton.url}
                                    text={logoutButton.text}
                                    toggleSidebar={toggleUserSidebar}
                                />
                            </SideBarItemWrapper>
                        )}
                    </Sidebar>
                )}
            </Header>
        </StickyContainer>
    )
}

export default memo(HeaderSection)
